import { generateClient } from "aws-amplify/api";
import {
  fetchUserAttributes,
  getCurrentUser,
  signOut,
  updateUserAttribute,
} from "aws-amplify/auth";

/* Sidebar bootstrap effects intentionally run only on mount. */
/* eslint-disable react-hooks/exhaustive-deps */
import { fetchAuthSession } from "aws-amplify/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import outputs from "../amplify_outputs.json";
import {
  createGeneralInformation,
  createOrganizationInformation,
} from "../graphql/mutations.js";
import {
  listGeneralInformations,
  listNotifications,
  listOrganizationCPs,
  listOrganizationInformations,
} from "../graphql/queries.js";
import {
  onCreateNotifications,
  onUpdateNotifications,
} from "../graphql/subscriptions.js";

import Assessment from "../ui-components/Assessment.js";
import ClientCreatePlan from "../ui-components/ClientCreatePlan.js";
import ClientTrackProgress from "../ui-components/ClientTrackProgress.js";
import ConnectedClientBar from "../ui-components/ConnectedClientBar.js"; // ✅
import { useConnectedClient } from "../ui-components/ConnectedClientContext.js";
import { useProgress } from "../Context/ProgressContextClean.js";
import CreatePlan from "../ui-components/CreatePlan.js";
import Dashboard from "../ui-components/Dashboard.js";
import DashboardOrganization from "../ui-components/DashboardOrganization.js";
import DashboardProfessional from "../ui-components/DashboardProfessional.js";
import GeneralInformation from "../ui-components/GeneralInformation.js";
import ManageConnections from "../ui-components/ManageConnections.js";
import NotificationsPopup from "../ui-components/NotificationsPopup.js";
import OrganizationInformation from "../ui-components/OrganizationInfomation.js";
import TrackProgress from "../ui-components/TrackProgress.js";
import ViewReports from "../ui-components/ViewReports.js";
import ClientViewReports from "./ClientViewReports.js";
import ManageEmployees from "./ManageEmployees.js";
import DashboardOE from "./DashboardOE.js";
import PaymentInformation from "./PaymentInformation.js";
import Resources from "./Resources.js";
import SentRecommendations from "./SentRecommendations.js";
import LabResultsAssessment from "../ui-components/LabResults.js";
import CoConnectHub from "../ui-components/CoConnectHub.js";
import AssessHub from "../ui-components/AssessHub.js";
import ExploreResultsHub from "../ui-components/ExploreResultsHub.js";
import Communities from "../ui-components/Communities.js";
import CommunityActivity from "../ui-components/CommunityActivity.js";
import PersonalProgress from "../ui-components/PersonalProgress.js";
import WholisticHealthProgram from "../ui-components/WholisticHealthProgram.js";
import WholisticHealthIntroduction from "../ui-components/WholisticHealthIntroduction.js";
import WholisticHealthExercise1 from "../ui-components/WholisticHealthExercise1.js";
import WholisticHealthExercise2 from "../ui-components/WholisticHealthExercise2.js";
import WholisticHealthExercise3 from "../ui-components/WholisticHealthExercise3.js";
import WholisticHealthExercise4 from "../ui-components/WholisticHealthExercise4.js";
import WholisticHealthExercise5 from "../ui-components/WholisticHealthExercise5.js";
import WholisticHealthExercise6 from "../ui-components/WholisticHealthExercise6.js";
import WholisticHealthExercise7 from "../ui-components/WholisticHealthExercise7.js";
import WholisticHealthExercise8 from "../ui-components/WholisticHealthExercise8.js";
import WholisticHealthExercise9 from "../ui-components/WholisticHealthExercise9.js";
import WholisticHealthExercise10 from "../ui-components/WholisticHealthExercise10.js";
import WholisticHealthConclusion from "../ui-components/WholisticHealthConclusion.js";

let authReadyPromise = null;
const GENERAL_NOTIFICATION_TYPES = [
  "ConnectionResponse",
  "ReviewReady",
  "Reviewed",
  "CommunityHideEscalation1",
  "CommunityHideEscalation2",
  "CommunityHideEscalation3",
  "CommunityAccessRevoked",
  "CommunityUnsafeConcern",
];

export async function waitForAuthReady() {
  if (!authReadyPromise) {
    authReadyPromise = (async () => {
      let session = null;
      for (let i = 0; i < 20; i++) {
        // try 20 times, ~2s total
        session = await fetchAuthSession({ forceRefresh: true }).catch(
          () => null,
        );
        if (session?.tokens?.accessToken) break;
        await new Promise((res) => setTimeout(res, 100));
      }
      return session;
    })();
  }
  return authReadyPromise;
}
const normalizeEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const COMPONENTS_MAP = {
  Dashboard,
  DashboardProfessional,
  GeneralInformation,
  Assessment,
  CreatePlan,
  TrackProgress,
  ViewReports,
  ManageConnections,
  OrganizationInformation,
  ManageEmployees,
  ClientCreatePlan,
  ClientTrackProgress,
  PaymentInformation,
  DashboardOrganization,
  SentRecommendations,
  ClientViewReports,
  Resources,
  DashboardOE,
  LabResultsAssessment,
  AssessHub,
  ExploreResultsHub,
  AssessmentReport: () => <ViewReports initialViewFilter="assessments" />,
  HealthPlanReport: () => <ViewReports initialViewFilter="trackPlans" />,
  LabResultsReport: () => <ViewReports initialViewFilter="labs" />,
  CompareReports: () => <ViewReports initialViewFilter="compare" />,
  CoConnectHub,
  Communities,
  CommunityActivity,
  PersonalProgress,
  WholisticHealthProgram,
  WholisticHealthIntroduction,
  WholisticHealthExercise1,
  WholisticHealthExercise2,
  WholisticHealthExercise3,
  WholisticHealthExercise4,
  WholisticHealthExercise5,
  WholisticHealthExercise6,
  WholisticHealthExercise7,
  WholisticHealthExercise8,
  WholisticHealthExercise9,
  WholisticHealthExercise10,
  WholisticHealthConclusion,
};
const client = generateClient();

const DEFAULT_VIEW = "Dashboard";

function getStoredView(userSub = "") {
  const startOn = localStorage.getItem("startOn");
  const savedViewForUser = userSub
    ? localStorage.getItem(`lastView:${userSub}`)
    : null;
  const savedViewGlobal = localStorage.getItem("lastView");

  return startOn || savedViewForUser || savedViewGlobal || DEFAULT_VIEW;
}

export default function SidebarWithContent() {
  // Ensure the Amplify client is created at render time (after Amplify.configure)

  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const outcomesLogoSrc = `${publicUrl}/Outcomes-Excellence-Logo-Badge-Grow-White.png`;

  const [showNotifications, setShowNotifications] = useState(false);
  const [open, setOpen] = useState(window.innerWidth >= 768);
  const [currentView, setCurrentView] = useState(() => getStoredView());
  const { connectedClient, setConnectedClient } = useConnectedClient();
  const { refresh: refreshProgressContext } = useProgress();
  const [accountType, setAccountType] = useState("");
  const [signedInAs, setSignedInAs] = useState(null);
  const [userId, setUserId] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [loading, setLoading] = useState(true); // ⬅️ Add this
  const [organizationName, setOrganizationName] = useState("");
  const [isInOrganization, setIsInOrganization] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [userAliases, setUserAliases] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [generalNotifications, setGeneralNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [isNotificationsRefreshing, setIsNotificationsRefreshing] =
    useState(false);
  const [hasHydratedConnectedClient, setHasHydratedConnectedClient] =
    useState(false);
  const notificationIntervalRef = useRef(null);
  const didPostAuthRefresh = useRef(false);
  const didAttemptAccountRecovery = useRef(false);
  const skipViewPersistenceRef = useRef(false);
  const connectedClientStorageKey = userId ? `connectedClient:${userId}` : null;

  const persistViewForReload = useCallback(
    (view = currentView) => {
      if (!view || skipViewPersistenceRef.current) return;

      try {
        localStorage.setItem("startOn", view);
        if (userId) {
          localStorage.setItem(`lastView:${userId}`, view);
        }
        localStorage.setItem("lastView", view);
      } catch (error) {
        /* ignore storage errors */
      }
    },
    [currentView, userId],
  );

  const fullHeight = () =>
    typeof window !== "undefined" && window.innerWidth < 768
      ? "100dvh"
      : "100vh";
  useEffect(() => {
    if (
      !connectedClient &&
      (currentView === "ClientViewReports" ||
        currentView === "ClientTrackProgress")
    ) {
      setCurrentView("DashboardProfessional");
    }
  }, [connectedClient, currentView]);
  useEffect(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [currentView]);
  useEffect(() => {
    const handleResize = () => setOpen(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!userId || !currentView) return;
    try {
      localStorage.setItem(`lastView:${userId}`, currentView);
      localStorage.setItem("lastView", currentView);
    } catch (e) {
      /* ignore storage errors */
    }
  }, [userId, currentView]);

  useEffect(() => {
    if (!currentView) return;

    const handlePageUnload = () => {
      persistViewForReload(currentView);
    };

    window.addEventListener("beforeunload", handlePageUnload);
    window.addEventListener("pagehide", handlePageUnload);

    return () => {
      window.removeEventListener("beforeunload", handlePageUnload);
      window.removeEventListener("pagehide", handlePageUnload);
    };
  }, [currentView, persistViewForReload]);

  useEffect(() => {
    let cancelled = false;
    async function loadAliases() {
      try {
        const [user, attrs] = await Promise.all([
          getCurrentUser(),
          fetchUserAttributes(),
        ]);
        if (cancelled) return;

        const aliases = [
          userId,
          user?.userId,
          user?.username,
          user?.attributes?.sub,
          user?.signInDetails?.loginId,
          attrs?.sub,
        ]
          .map((v) => (v === null || v === undefined ? "" : String(v).trim()))
          .filter(Boolean);

        setUserAliases(Array.from(new Set(aliases)));
      } catch {
        if (!cancelled) {
          setUserAliases(userId ? [String(userId)] : []);
        }
      }
    }

    if (userId) {
      loadAliases();
    } else {
      setUserAliases([]);
    }

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (didPostAuthRefresh.current) return;
    if (loading || !userId) return;
    didPostAuthRefresh.current = true;
    refreshProgressContext && refreshProgressContext();
  }, [loading, userId, refreshProgressContext]);
  useEffect(() => {
    const recoveryUrl = outputs.custom?.accountRecovery?.url;
    if (!userId || !recoveryUrl || didAttemptAccountRecovery.current) return;

    didAttemptAccountRecovery.current = true;
    async function recoverRecreatedAccount() {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) return;
        const recoveryEndpoint = recoveryUrl.replace(/\/$/, "") + "/recover";
        const recoveryResponse = await fetch(recoveryEndpoint, {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!recoveryResponse.ok) {
          throw new Error(
            `Account recovery request failed (${recoveryResponse.status})`,
          );
        }
      } catch (error) {
        console.warn("Account recovery check could not be completed:", error);
      }
    }

    recoverRecreatedAccount();
  }, [userId]);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [isOrgPrimaryAdmin, setIsOrgPrimaryAdmin] = useState(false);

  const refreshNotifications = useCallback(
    async (isManual = false) => {
      if (!notificationEmail && !userId) {
        setRecommendations([]);
        setGeneralNotifications([]);
        return;
      }

      if (isManual) {
        setNotificationsLoading(true);
      } else {
        setIsNotificationsRefreshing(true);
      }

      try {
        const notificationTypeFilter =
          accountType === "Professional" && signedInAs !== "Individual"
            ? "RecommendationBack"
            : "Recommendation";

        const targetFilters = [];
        if (notificationEmail) {
          targetFilters.push({ TargetEmail: { eq: notificationEmail } });
        }
        (userAliases.length > 0 ? userAliases : userId ? [userId] : []).forEach(
          (alias) => {
            targetFilters.push({ TargetUserId: { eq: String(alias) } });
          },
        );

        const targetScopeFilter =
          targetFilters.length === 1 ? targetFilters[0] : { or: targetFilters };

        const [recRes, genRes] = await Promise.all([
          client.graphql({
            authMode: "userPool",
            query: listNotifications,
            variables: {
              filter: {
                and: [
                  targetScopeFilter,
                  { Status: { eq: "pending" } },
                  { NotificationType: { eq: notificationTypeFilter } },
                ],
              },
              limit: 200,
            },
          }),
          client.graphql({
            authMode: "userPool",
            query: listNotifications,
            variables: {
              filter: {
                and: [
                  targetScopeFilter,
                  { Status: { eq: "pending" } },
                  {
                    or: GENERAL_NOTIFICATION_TYPES.map((type) => ({
                      NotificationType: { eq: type },
                    })),
                  },
                ],
              },
              limit: 200,
            },
          }),
        ]);

        setRecommendations(
          recRes?.data?.listNotifications?.items?.filter(Boolean) || [],
        );
        setGeneralNotifications(
          genRes?.data?.listNotifications?.items?.filter(Boolean) || [],
        );
      } catch (error) {
        console.warn("Could not load notifications:", error);
        setRecommendations([]);
        setGeneralNotifications([]);
      } finally {
        if (isManual) {
          setNotificationsLoading(false);
        } else {
          setIsNotificationsRefreshing(false);
        }
      }
    },
    [accountType, notificationEmail, signedInAs, userId, userAliases],
  );

  useEffect(() => {
    if (!notificationEmail && !userId) {
      if (notificationIntervalRef.current) {
        clearInterval(notificationIntervalRef.current);
        notificationIntervalRef.current = null;
      }
      return;
    }

    refreshNotifications(false);

    notificationIntervalRef.current = setInterval(() => {
      refreshNotifications(false);
    }, 10000);

    return () => {
      clearInterval(notificationIntervalRef.current);
      notificationIntervalRef.current = null;
    };
  }, [notificationEmail, refreshNotifications, userId]);

  useEffect(() => {
    if (!userId) return;

    const subscriptionVariables = {
      TargetUserId: userId,
    };

    const createSubscription = client
      .graphql({
        authMode: "userPool",
        query: onCreateNotifications,
        variables: subscriptionVariables,
      })
      .subscribe({
        next: () => {
          refreshNotifications(false);
        },
        error: (error) => {
          console.warn("Notification create subscription error:", error);
        },
      });

    const updateSubscription = client
      .graphql({
        authMode: "userPool",
        query: onUpdateNotifications,
        variables: subscriptionVariables,
      })
      .subscribe({
        next: () => {
          refreshNotifications(false);
        },
        error: (error) => {
          console.warn("Notification update subscription error:", error);
        },
      });

    return () => {
      createSubscription.unsubscribe();
      updateSubscription.unsubscribe();
    };
  }, [refreshNotifications, userId]);

  useEffect(() => {
    if (!showNotifications || !notificationEmail) return;
    refreshNotifications(true);
  }, [showNotifications, notificationEmail, refreshNotifications]);

  useEffect(() => {
    if (!userId) return;

    if (accountType !== "Professional" || signedInAs !== "Professional") {
      setConnectedClient(null);
      setHasHydratedConnectedClient(true);
      return;
    }

    const stored = localStorage.getItem(connectedClientStorageKey);
    if (!stored) {
      setHasHydratedConnectedClient(true);
      return;
    }

    try {
      setConnectedClient(JSON.parse(stored));
    } catch (error) {
      console.warn("Failed to restore connected client:", error);
      localStorage.removeItem(connectedClientStorageKey);
    } finally {
      setHasHydratedConnectedClient(true);
    }
  }, [
    accountType,
    connectedClientStorageKey,
    setConnectedClient,
    signedInAs,
    userId,
  ]);

  useEffect(() => {
    if (!hasHydratedConnectedClient || !connectedClientStorageKey) return;

    if (accountType !== "Professional" || signedInAs !== "Professional") {
      localStorage.removeItem(connectedClientStorageKey);
      return;
    }

    if (connectedClient) {
      localStorage.setItem(
        connectedClientStorageKey,
        JSON.stringify(connectedClient),
      );
    } else {
      localStorage.removeItem(connectedClientStorageKey);
    }
  }, [
    accountType,
    connectedClient,
    connectedClientStorageKey,
    hasHydratedConnectedClient,
    signedInAs,
  ]);

  useEffect(() => {
    const loadUserAttributes = async () => {
      try {
        const session = await waitForAuthReady(); // ✅ wait for auth
        if (!session?.tokens?.accessToken) {
          console.warn("Auth failed to load after multiple attempts");
          setLoading(false);
          return;
        }

        const attrs = await fetchUserAttributes();
        const acctType = attrs["custom:accountType"];
        const signedIn = attrs["custom:SignedInAs"];
        const userSub = attrs.sub;
        const email = String(attrs.email || "")
          .trim()
          .toLowerCase();
        const groups = new Set([
          ...(session?.tokens?.idToken?.payload?.["cognito:groups"] || []),
          ...(session?.tokens?.accessToken?.payload?.["cognito:groups"] || []),
        ]);

        setAccountType(acctType);
        setUserId(userSub);
        setSignedInAs(signedIn || acctType);
        setNotificationEmail(email);
        setIsAdmin(groups.has("Admins"));

        if (acctType === "Organization") {
          try {
            const existingOrgRes = await client.graphql({
              query: listOrganizationInformations,
              variables: {
                filter: { userId: { eq: userSub } },
                limit: 1,
              },
              authMode: "userPool",
            });

            const existingOrg =
              existingOrgRes?.data?.listOrganizationInformations?.items?.[0];

            if (!existingOrg) {
              await client.graphql({
                query: createOrganizationInformation,
                variables: {
                  input: {
                    userId: userSub,
                    organizationId: userSub,
                    BusinessName: attrs["custom:BusinessName"] || null,
                    BusinessEIN: attrs["custom:BusinessEIN"]
                      ? Number(attrs["custom:BusinessEIN"])
                      : null,
                    BusinessCountry: attrs["custom:Country"] || null,
                    BusinessAddress: attrs.address || null,
                    BusinessCity: attrs["custom:City"] || null,
                    BusinessState: attrs["custom:State"] || null,
                    BusinessZipCode: attrs["custom:ZipCode"]
                      ? Number(attrs["custom:ZipCode"])
                      : null,
                    BusinessPhoneNumber: attrs["custom:PhoneNumber"] || null,
                    BusinessWebsite: attrs.website || null,
                    Departments: [],
                    ProgramsUnits: [],
                    Services: [],
                  },
                },
                authMode: "userPool",
              });
            }
          } catch (orgCreateErr) {
            console.error(
              "Error ensuring OrganizationInformation exists:",
              orgCreateErr,
            );
          }
        } else {
          try {
            const existingGiRes = await client.graphql({
              query: listGeneralInformations,
              variables: {
                filter: {
                  or: [
                    { userId: { eq: userSub } },
                    { Email: { eq: normalizeEmail(email) } },
                  ],
                },
                limit: 5,
              },
              authMode: "userPool",
            });

            const existingGiItems =
              existingGiRes?.data?.listGeneralInformations?.items || [];
            const hasOwnedProfile = existingGiItems.some(
              (item) => String(item?.userId || "").trim() === userSub,
            );

            if (!hasOwnedProfile) {
              await client.graphql({
                query: createGeneralInformation,
                variables: {
                  input: {
                    userId: userSub,
                    FirstName: attrs.given_name || "",
                    LastName: attrs.family_name || "",
                    Email: normalizeEmail(email),
                    ProfileType: acctType || "Individual",
                    organizationId: attrs["custom:organizationId"] || null,
                  },
                },
                authMode: "userPool",
              });
            }
          } catch (generalInfoCreateErr) {
            console.error(
              "Error ensuring GeneralInformation exists:",
              generalInfoCreateErr,
            );
          }
        }

        let defaultView = DEFAULT_VIEW; // fallback

        if (acctType === "Organization") {
          try {
            const orgRes = await client.graphql({
              query: listOrganizationInformations,
              variables: {
                filter: {
                  userId: { eq: userSub },
                },
              },
              authMode: "userPool",
            });
            const orgItem =
              orgRes?.data?.listOrganizationInformations?.items?.[0];

            if (orgItem?.BusinessName) {
              setOrganizationName(orgItem.BusinessName);
            }

            defaultView = orgItem
              ? "DashboardOrganization"
              : "OrganizationInformation";
          } catch (err) {
            console.error("Error loading org info:", err);
            defaultView = "OrganizationInformation";
          }
        } else if (acctType === "Professional") {
          try {
            const infoRes = await client.graphql({
              query: listGeneralInformations,
              variables: {
                filter: {
                  userId: { eq: userSub },
                },
              },
              authMode: "userPool",
            });
            const infoItem = infoRes?.data?.listGeneralInformations?.items?.[0];

            const hasGeneralInfo = !!infoItem;

            if (signedIn === "Professional") {
              defaultView = hasGeneralInfo
                ? "DashboardProfessional"
                : "GeneralInformation";
            } else {
              defaultView = hasGeneralInfo ? "Dashboard" : "GeneralInformation";
            }
          } catch (err) {
            console.error("Error loading general info:", err);
            defaultView = "GeneralInformation";
          }
        } else {
          defaultView = DEFAULT_VIEW;
        }

        const savedView = getStoredView(userSub);
        const allowedViewsByAccountType =
          acctType === "Organization"
            ? new Set([
                "DashboardOE",
                "DashboardOrganization",
                "OrganizationInformation",
                "ManageEmployees",
                "PaymentInformation",
                "Resources",
              ])
            : acctType === "Professional"
              ? signedIn === "Professional"
                ? new Set([
                    "DashboardProfessional",
                    "GeneralInformation",
                    "ManageConnections",
                    "SentRecommendations",
                    "ClientTrackProgress",
                    "ClientViewReports",
                    "PaymentInformation",
                    "Resources",
                    "OrganizationInformation",
                    "ManageEmployees",
                  ])
                : new Set([
                    "Dashboard",
                    "GeneralInformation",
                    "AssessHub",
                    "CreatePlan",
                    "TrackProgress",
                    "CoConnectHub",
                    "ExploreResultsHub",
                    "Resources",
                  ])
              : new Set([
                  "Dashboard",
                  "GeneralInformation",
                  "AssessHub",
                  "CreatePlan",
                  "TrackProgress",
                  "CoConnectHub",
                  "ExploreResultsHub",
                  "Resources",
                ]);
        const nextView =
          savedView &&
          COMPONENTS_MAP[savedView] &&
          (!allowedViewsByAccountType ||
            allowedViewsByAccountType.has(savedView))
            ? savedView
            : defaultView;

        setCurrentView(nextView);
        localStorage.removeItem("startOn");

        // ✅ Fetch org BusinessName if Organization
        if (acctType === "Organization") {
          try {
            const orgRes = await client.graphql({
              query: listOrganizationInformations,
              variables: {
                filter: {
                  userId: { eq: userSub },
                },
              },
              authMode: "userPool",
            });
            const orgItem =
              orgRes?.data?.listOrganizationInformations?.items?.[0];
            if (orgItem?.BusinessName) {
              setOrganizationName(orgItem.BusinessName);
            }
          } catch (err) {
            console.error("Error loading org info:", err);
          }
        }

        // ✅ Check if user is also an Organization Administrator
        if (acctType === "Professional" && email) {
          try {
            const result = await client.graphql({
              query: listOrganizationCPs,
              variables: {
                filter: {
                  OrganizationCPEmail: { eq: email },
                },
              },
              authMode: "userPool",
            });

            const matches = result?.data?.listOrganizationCPs?.items || [];

            // If the professional is org-claimed, display the org BusinessName.
            // This uses the OrganizationInformation.orgId -> org-claim read rule.
            const orgIdFromClaim = attrs["custom:organizationId"] || "";
            const orgIdFromCP = matches?.[0]?.OrganizationId || "";
            const effectiveOrgId = orgIdFromClaim || orgIdFromCP;
            if (effectiveOrgId && !organizationName) {
              try {
                const orgRes = await client.graphql({
                  query: listOrganizationInformations,
                  variables: {
                    filter: { organizationId: { eq: effectiveOrgId } },
                    limit: 1,
                  },
                  authMode: "userPool",
                });
                const orgItem =
                  orgRes?.data?.listOrganizationInformations?.items?.[0];
                if (orgItem?.BusinessName) {
                  setOrganizationName(orgItem.BusinessName);
                } else {
                  setOrganizationName("Organization");
                }
              } catch (e) {
                console.warn("Could not load org BusinessName:", e);
                setOrganizationName("Organization");
              }
            }

            for (const match of matches) {
              if (match.OrganizationAdministrator) {
                setIsOrgAdmin(true);
              }

              if (match.PrimaryOrganizationAdministrator) {
                setIsOrgPrimaryAdmin(true);
              }
            }
            const isInOrganization = matches.length > 0;
            setIsInOrganization(isInOrganization); // you will need to add this state
          } catch (err) {
            console.error("Error checking OrganizationCPs:", err);
          }
        }
      } catch (err) {
        console.error("Error fetching attributes:", err);
      } finally {
        setLoading(false);
      }
    };

    loadUserAttributes();
    // This bootstrap determines organizationName, so it must only run on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleSignedInAs = async () => {
    const newRole =
      signedInAs === "Professional" ? "Individual" : "Professional";
    try {
      await updateUserAttribute({
        userAttribute: { attributeKey: "custom:SignedInAs", value: newRole },
      });

      // Disconnect any connected client if switching away from Professional
      if (newRole === "Individual") {
        setConnectedClient(null);
        if (connectedClientStorageKey) {
          localStorage.removeItem(connectedClientStorageKey);
        }
      }

      setSignedInAs(newRole);

      // 🔄 Dynamically check for GeneralInformation before routing
      try {
        const infoRes = await client.graphql({
          query: listGeneralInformations,
          variables: {
            filter: {
              or: [{ owner: { eq: userId } }, { userId: { eq: userId } }],
            },
          },
          authMode: "userPool",
        });

        const infoItem = infoRes?.data?.listGeneralInformations?.items?.[0];
        const hasGeneralInfo = !!infoItem;

        const newView =
          newRole === "Professional"
            ? hasGeneralInfo
              ? "DashboardProfessional"
              : "GeneralInformation"
            : hasGeneralInfo
              ? "Dashboard"
              : "GeneralInformation";

        setCurrentView(newView);
      } catch (err) {
        console.error("Error checking general info on switch:", err);
        setCurrentView("GeneralInformation"); // fallback just in case
      }
    } catch (err) {
      console.error("Error updating SignedInAs:", err);
    }
  };

  const handleSignOut = async () => {
    try {
      const ok = window.confirm("Are you sure you want to sign out?");
      if (!ok) return;
      skipViewPersistenceRef.current = true;
      setShowNotifications(false);
      setConnectedClient(null);
      localStorage.removeItem("startOn");
      if (userId) {
        localStorage.removeItem(`lastView:${userId}`);
      }
      localStorage.removeItem("lastView");
      localStorage.removeItem("connectedClient");
      if (connectedClientStorageKey) {
        localStorage.removeItem(connectedClientStorageKey);
      }
      await signOut();
      window.location.reload();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const navItems = [];
  const stepLabel = (stepNumber, text) => ({
    label: text,
    labelNode: (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 8,
          width: "100%",
        }}
      >
        <img
          src={(process.env.PUBLIC_URL || "") + `/${stepNumber}.png`}
          alt={`Step ${stepNumber}`}
          style={{ width: 22, height: 22, objectFit: "contain" }}
        />
        <span>{text}</span>
      </span>
    ),
  });

  if (accountType === "Organization") {
    // ✅ Only show these for Organization accounts
    if (isAdmin) {
      navItems.push({ label: "Master Dashboard", view: "DashboardOE" });
    }
    navItems.push(
      { label: "Organization Dashboard", view: "DashboardOrganization" },
      { label: "Organization Information", view: "OrganizationInformation" },
      { label: "Manage Employees", view: "ManageEmployees" },
      { label: "Payment Information", view: "PaymentInformation" },
    );
    navItems.push({ label: "Resources", view: "Resources" });
  } else {
    if (accountType === "Professional") {
      navItems.push({
        label:
          signedInAs === "Professional"
            ? "Switch to Individual"
            : "Switch to Professional",
        action: () => {
          setShowNotifications(false); // ✅ Close the popup
          handleToggleSignedInAs(); // ✅ Then switch
        },
      });
    }
    // ✅ Show switch button for Individuals or Professionals

    if (signedInAs === "Professional") {
      navItems.push(
        { label: "Professional Dashboard", view: "DashboardProfessional" },
        { label: "General Information", view: "GeneralInformation" },
        { label: "Manage Connections", view: "ManageConnections" },
        { label: "Sent Recommendations", view: "SentRecommendations" },
      );
      if (!isInOrganization) {
        navItems.push({
          label: "Payment Information",
          view: "PaymentInformation",
        });
      }
      if (connectedClient) {
        navItems.push({
          label: "View Client Progress",
          view: "ClientTrackProgress",
        });
      }
      if (connectedClient) {
        navItems.push({
          label: "View Client Report",
          view: "ClientViewReports",
        });
      }
      navItems.push({ label: "Resources", view: "Resources" });

      if (isOrgAdmin || isOrgPrimaryAdmin) {
        navItems.push(
          { divider: true },
          {
            label: "Organization Information",
            view: "OrganizationInformation",
          },
          { label: "Manage Employees", view: "ManageEmployees" },
        );
      }

      // Add Payment Information for Professionals NOT in an org
    } else {
      navItems.push(
        { label: "Dashboard", view: "Dashboard" },
        { label: "General Information", view: "GeneralInformation" },
        { ...stepLabel(1, "Assess"), view: "AssessHub" },
        { ...stepLabel(2, "Plan"), view: "CreatePlan" },
        { ...stepLabel(3, "Progress"), view: "TrackProgress" },
        { ...stepLabel(4, "CoConnect™"), view: "CoConnectHub" },
        { ...stepLabel(5, "Explore Results"), view: "ExploreResultsHub" },
        { label: "Resources", view: "Resources" },
      );
    }
  }

  const allowedViews = new Set(
    navItems.filter((item) => item?.view).map((item) => item.view),
  );
  if (accountType !== "Organization" && signedInAs !== "Professional") {
    [
      "Assessment",
      "LabResultsAssessment",
      "ManageConnections",
      "Communities",
      "CommunityActivity",
      "PersonalProgress",
      "AssessmentReport",
      "HealthPlanReport",
      "LabResultsReport",
      "CompareReports",
    ].forEach((view) => allowedViews.add(view));
  }
  [
    "WholisticHealthProgram",
    "WholisticHealthIntroduction",
    "WholisticHealthExercise1",
    "WholisticHealthExercise2",
    "WholisticHealthExercise3",
    "WholisticHealthExercise4",
    "WholisticHealthExercise5",
    "WholisticHealthExercise6",
    "WholisticHealthExercise7",
    "WholisticHealthExercise8",
    "WholisticHealthExercise9",
    "WholisticHealthExercise10",
    "WholisticHealthConclusion",
  ].forEach((view) => allowedViews.add(view));

  useEffect(() => {
    if (!accountType || !currentView) return;
    if (allowedViews.size === 0) return;
    if (allowedViews.has(currentView)) return;

    const fallbackView = navItems.find((item) => item?.view)?.view;
    if (fallbackView) {
      setCurrentView(fallbackView);
    }
  }, [accountType, currentView, navItems, allowedViews]);

  const handleClick = (view) => {
    setCurrentView(view);
    if (window.innerWidth < 768) setOpen(false);
  };

  const SelectedComponent = COMPONENTS_MAP[currentView];
  const notificationCount =
    recommendations.length + generalNotifications.length;

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 16,
          height: fullHeight(),
        }}
      >
        <img
          src={outcomesLogoSrc}
          alt="Logo"
          style={{ width: 140, height: "auto" }}
        />
        <div
          style={{
            width: 48,
            height: 48,
            border: "6px solid #ccc",
            borderTop: "6px solid #3498db",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    );
  }

  return (
    <>
      <ConnectedClientBar /> {/* ✅ Only renders if a client is connected */}
      {/* Hamburger Toggle */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          position: "fixed",
          top: open ? 56 : 16,
          left: open ? 120 : 16, // ← when open, move it to the right edge of sidebar, else more to the left
          zIndex: 1001,
          backgroundColor: "#0067B3",
          color: "white",
          padding: "6px 12px",
          borderRadius: 30,
          cursor: "pointer",
          fontSize: 20,
          fontWeight: "bold",
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
          userSelect: "none",
          transition: "left 0.3s ease", // ← smooth slide
        }}
      >
        ☰
      </div>
      {/* Sidebar */}
      <nav
        aria-label="Sidebar"
        style={{
          position: "fixed",
          top: 0,
          left: open ? 0 : "-280px",
          height: fullHeight(),
          width: 280,
          backgroundColor: "#0067B3",
          color: "white",
          transition: "left 0.3s ease",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: "center", padding: "16px 16px 0" }}>
          <img
            src={outcomesLogoSrc}
            alt="Logo"
            style={{ width: 100, height: "auto", marginBottom: 16 }}
          />
        </div>

        {/* Scrollable nav items */}
        <div
          style={{
            flexGrow: 1,
            overflowY: "auto",
            padding: "0 16px",
            scrollbarWidth: "none",
          }}
        >
          <style>{`nav::-webkit-scrollbar { display: none; }`}</style>
          {navItems.map((item, i) => {
            if (item.divider) {
              return (
                <div key={`divider-${i}`}>
                  <div
                    style={{
                      height: 1,
                      backgroundColor: "#ccc",
                      margin: "16px 0 8px",
                      opacity: 0.5,
                    }}
                  />
                  {organizationName && (
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: 14,
                        color: "white",
                        marginBottom: 8,
                      }}
                    >
                      {organizationName}
                    </div>
                  )}
                </div>
              );
            }

            const isSwitchButton =
              typeof item.label === "string" &&
              item.label.includes("Switch to");

            return (
              <button
                key={item.view || item.label || i}
                onClick={() =>
                  item.action ? item.action() : handleClick(item.view)
                }
                style={{
                  backgroundColor:
                    currentView === item.view ? "#e0f0ff" : "white",
                  color: "#0067B3",
                  border: isSwitchButton ? "3px dotted #0067B3" : "none",
                  borderRadius: 8,
                  padding: "12px",
                  marginBottom: 12,
                  fontWeight: "bold",
                  fontSize: 15,
                  width: "100%",
                  textAlign: "left",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                  cursor: "pointer",
                }}
              >
                {item.labelNode || item.label}
              </button>
            );
          })}
        </div>

        {/* Bottom section */}
        <div
          style={{ padding: 16, borderTop: "1px solid rgba(255,255,255,0.2)" }}
        >
          {/* Info Toggle */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: 14,
                marginBottom: 8,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              onClick={() => setShowInfo(!showInfo)}
            >
              <span>Information</span>
              <span
                style={{
                  transform: showInfo ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              >
                ▼
              </span>
            </div>

            <div
              style={{
                maxHeight: showInfo ? 500 : 0,
                overflow: "hidden",
                transition: "max-height 0.4s ease",
              }}
            >
              {[
                {
                  label: "info@OutcomesExcellence.org",
                  href: "mailto:info@OutcomesExcellence.org",
                },
                {
                  label: "Provide Feedback",
                  href: "https://forms.microsoft.com/r/s0uDENAjzW",
                },
                {
                  label: "Privacy Policy",
                  href: "https://outcomesexcellence.com/images/Documents/OE%20Privacy%20Policy.pdf",
                },
                {
                  label: "Terms and Conditions",
                  href: "https://outcomesexcellence.com/images/Documents/OE%20Terms%20and%20Conditions.pdf",
                },
                {
                  label: "© Outcomes Excellence, Inc.",
                  href: "https://outcomesexcellence.com/",
                },
                {
                  label: `Account Type: ${accountType || "Loading..."}`,
                  href: null,
                },
                { label: `User ID: ${userId || "Loading..."}`, href: null },
              ].map((item, i) => (
                <div key={i} style={{ marginBottom: 6 }}>
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "white",
                        textDecoration: "underline",
                        fontSize: 12,
                      }}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <span style={{ color: "white", fontSize: 13 }}>
                      {item.label}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notifications Button */}
          <button
            onClick={() => setShowNotifications(true)}
            style={{
              backgroundColor: "#fff",
              color: "#0067B3",
              borderRadius: 8,
              padding: 10,
              width: "100%",
              fontWeight: "bold",
              fontSize: 14,
              marginBottom: 10,
              cursor: "pointer",
            }}
          >
            🔔 Notifications ({notificationCount})
          </button>

          {/* Sign Out Button */}
          <button
            onClick={handleSignOut}
            style={{
              backgroundColor: "#fff",
              color: "#0067B3",
              borderRadius: 8,
              padding: 10,
              width: "100%",
              fontWeight: "bold",
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Sign Out
          </button>
        </div>
      </nav>
      {/* Main content area */}
      <main
        style={{
          marginLeft: open ? 280 : 0,
          padding: 24,
          minHeight: "100vh",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center", // ✅ horizontally center
          justifyContent: "flex-start", // or 'center' if you want vertical center too
          transition: "margin-left 0.3s ease",
        }}
      >
        {SelectedComponent ? (
          <SelectedComponent
            setCurrentView={setCurrentView}
            accountType={accountType}
            signedInAs={signedInAs}
            sidebarOpen={open}
          />
        ) : (
          <div>Component not found</div>
        )}
      </main>
      <NotificationsPopup
        visible={showNotifications}
        onClose={() => setShowNotifications(false)}
        recommendations={recommendations}
        generalNotifications={generalNotifications}
        onRefresh={refreshNotifications}
        loading={notificationsLoading}
        isAutoRefreshing={isNotificationsRefreshing}
      />
    </>
  );
}
